import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { MOULDING_PROFILES } from "@/data/site";
import QuoteCTA from "@/components/QuoteCTA";
import { Reveal, Kicker, MaskedLine } from "@/components/Reveal";

export default function MouldingDetail() {
  const { slug } = useParams();
  const m = MOULDING_PROFILES.find((x) => x.slug === slug);
  const others = MOULDING_PROFILES.filter((x) => x.slug !== slug);

  if (!m)
    return (
      <div className="px-5 pb-32 pt-48 text-center" data-testid="moulding-not-found">
        <p className="font-display text-4xl">Moulding profile not found.</p>
        <Link to="/timber" className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-kwila">
          <ArrowLeft size={15} /> Back to Timber
        </Link>
      </div>
    );

  return (
    <div data-testid="moulding-detail-page">
      <section className="px-5 pb-16 pt-32 md:px-10 md:pt-40">
        <div className="mx-auto max-w-[1100px]">
          <Link
            to="/timber"
            data-testid="moulding-back-link"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-sand transition-colors hover:text-kwila"
          >
            <ArrowLeft size={14} /> Timber Mouldings
          </Link>
          <div className="mt-8">
            <Kicker>Export Moulding Profile</Kicker>
            <h1 className="mt-4 font-display text-4xl font-light tracking-tight md:text-6xl">
              <MaskedLine delay={0.1}>{m.name}</MaskedLine>
            </h1>
          </div>
        </div>
      </section>

      <section className="px-5 pb-24 md:px-10">
        <div className="mx-auto grid max-w-[1100px] gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="rounded-sm border border-ink/10 bg-stone/60 p-10 md:p-16" data-testid="moulding-profile-large">
              <img
                src={m.img}
                alt={`${m.name} — profile drawing`}
                className="h-48 w-full object-contain md:h-64"
              />
            </div>
            <p className="mt-3 text-center text-[11px] uppercase tracking-[0.2em] text-sand">
              Profile drawing — not to scale
            </p>
          </Reveal>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="text-base leading-relaxed text-ink/70">{m.desc}</p>
              <div className="mt-8 divide-y divide-ink/10 border-y border-ink/10" data-testid="moulding-specs">
                <div className="grid grid-cols-3 gap-4 py-3.5 text-sm">
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-sand">Sizes</span>
                  <span className="col-span-2 text-ink/80">{m.sizes.join("  ·  ")}</span>
                </div>
                <div className="grid grid-cols-3 gap-4 py-3.5 text-sm">
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-sand">Species</span>
                  <span className="col-span-2 text-ink/80">Kwila (Merbau) and selected tropical hardwoods</span>
                </div>
                <div className="grid grid-cols-3 gap-4 py-3.5 text-sm">
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-sand">Supply</span>
                  <span className="col-span-2 text-ink/80">Export packs, quoted to volume and length. [Lead time to be confirmed]</span>
                </div>
              </div>
              <Link
                to="/request-quote"
                data-testid="moulding-quote-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-kwila"
              >
                Request a Quote <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-stone/60 px-5 py-20 md:px-10" data-testid="moulding-others">
        <div className="mx-auto max-w-[1100px]">
          <Reveal className="mb-8">
            <Kicker>Other Profiles</Kicker>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/timber/mouldings/${o.slug}`}
                data-testid={`moulding-other-${o.slug}`}
                className="group block rounded-sm bg-cream p-4 transition-shadow hover:ring-2 hover:ring-kwila"
              >
                <div className="flex h-16 items-center justify-center">
                  <img src={o.img} alt={`${o.name} profile drawing`} loading="lazy" className="max-h-full w-full object-contain" />
                </div>
                <p className="mt-3 border-t border-ink/10 pt-2 text-center text-[11px] font-bold uppercase tracking-[0.12em]">
                  {o.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <QuoteCTA title="Need this profile in volume?" copy="Send the profile name, sizes, quantities and delivery location — mouldings are machined to order in PNG and shipped from Archerfield." />
    </div>
  );
}
